import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, Building2, UserCheck, Workflow, RefreshCw } from "lucide-react";

function isSuperAdmin(user) {
    return (user?.role || "").trim().toLowerCase().replace("_", " ") === "super admin";
}

function KpiTile({ icon: Icon, label, value, sub }) {
    return (
        <div className="border border-border bg-card p-5" style={{ borderRadius: 2 }}>
            <div className="flex items-center gap-2 text-[10.5px] font-bold tracking-[0.14em] uppercase text-muted-foreground">
                <Icon className="w-3.5 h-3.5" strokeWidth={1.6} />
                {label}
            </div>
            <div className="mt-3 font-display text-4xl tabular-nums" style={{ fontWeight: 500 }}>
                {value}
            </div>
            {sub && <div className="mt-1 text-[11.5px] text-muted-foreground font-mono">{sub}</div>}
        </div>
    );
}

function Section({ title, kicker, children, right }) {
    return (
        <div className="mt-8">
            <div className="flex items-end justify-between mb-3">
                <div>
                    <div className="text-[9.5px] font-bold tracking-[0.18em] uppercase text-gold">{kicker}</div>
                    <h2 className="font-display text-2xl mt-0.5">{title}</h2>
                </div>
                {right}
            </div>
            {children}
        </div>
    );
}

export default function AdminMetrics() {
    const { user } = useAuth();
    const [workspaces, setWorkspaces] = useState(null);
    const [demo, setDemo] = useState(null);
    const [funnel, setFunnel] = useState(null);
    const [demoWindow, setDemoWindow] = useState("7");
    const [funnelWindow, setFunnelWindow] = useState("30");
    const [includeInternal, setIncludeInternal] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const [w, d, f] = await Promise.all([
                api.get(`/admin/metrics/workspaces?include_internal=${includeInternal}`),
                api.get(`/admin/metrics/demo-activity?days=${demoWindow}`),
                api.get(`/admin/metrics/signup-funnel?days=${funnelWindow}`),
            ]);
            setWorkspaces(w.data);
            setDemo(d.data);
            setFunnel(f.data);
        } finally {
            setLoading(false);
        }
    }, [demoWindow, funnelWindow, includeInternal]);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const [w, d, f] = await Promise.all([
                    api.get(`/admin/metrics/workspaces?include_internal=${includeInternal}`),
                    api.get(`/admin/metrics/demo-activity?days=${demoWindow}`),
                    api.get(`/admin/metrics/signup-funnel?days=${funnelWindow}`),
                ]);
                if (!active) return;
                setWorkspaces(w.data);
                setDemo(d.data);
                setFunnel(f.data);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [demoWindow, funnelWindow, includeInternal]);

    if (!isSuperAdmin(user)) return <Navigate to="/" replace />;

    return (
        <>
            <PageHeader
                kicker="ADMIN · METRICS"
                title="Workspace metrics"
                subtitle="Live operational view — workspaces, demo activity, signup funnel."
                actions={
                    <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-refresh">
                        <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                }
            />
            <PageBody>
                {/* KPI ROW */}
                <div className="grid grid-cols-4 gap-3" data-testid="admin-kpi-row">
                    <KpiTile
                        icon={Building2}
                        label="Real customer orgs"
                        value={workspaces?.summary?.real_customers ?? "—"}
                        sub={`of ${workspaces?.summary?.total ?? "—"} total (${workspaces?.summary?.internal ?? "—"} internal)`}
                    />
                    <KpiTile
                        icon={UserCheck}
                        label={`Demo logins · last ${demoWindow}d`}
                        value={demo?.total_logins ?? "—"}
                        sub={`${demo?.distinct_ips ?? 0} unique IPs · ${demo?.distinct_browsers ?? 0} browsers`}
                    />
                    <KpiTile
                        icon={Workflow}
                        label={`Signup attempts · last ${funnelWindow}d`}
                        value={funnel?.unique_emails_attempted ?? "—"}
                        sub={`${funnel?.counts?.signup_started ?? 0} started · ${funnel?.counts?.signup_completed ?? 0} completed`}
                    />
                    <KpiTile
                        icon={Activity}
                        label="Signup success rate"
                        value={`${funnel?.success_rate_pct ?? 0}%`}
                        sub={`${funnel?.counts?.signup_failed ?? 0} failed in ${funnelWindow}d`}
                    />
                </div>

                {/* WORKSPACES TABLE */}
                <Section
                    kicker="WORKSPACES · ALL ORGANISATIONS"
                    title="Every workspace created on Vyaahar"
                    right={
                        <label className="flex items-center gap-2 text-[12px] font-mono text-muted-foreground select-none">
                            <input
                                type="checkbox"
                                checked={includeInternal}
                                onChange={(e) => setIncludeInternal(e.target.checked)}
                                data-testid="include-internal-toggle"
                            />
                            include demo / test / Vyastics
                        </label>
                    }
                >
                    <div className="border border-border bg-card overflow-x-auto" style={{ borderRadius: 2 }}>
                        <table className="w-full text-[12.5px]">
                            <thead className="bg-muted/40 text-muted-foreground">
                                <tr className="text-left">
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-wider">Created</th>
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-wider">Name</th>
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-wider">Owner email</th>
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-wider">Domain</th>
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-wider">GSTIN</th>
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-wider">Source</th>
                                </tr>
                            </thead>
                            <tbody data-testid="workspaces-table-body">
                                {(workspaces?.rows || []).map((r) => (
                                    <tr key={r.id} className="border-t border-border">
                                        <td className="px-3 py-2 font-mono">{r.created_at ? r.created_at.slice(0, 10) : "—"}</td>
                                        <td className="px-3 py-2">
                                            <div className="font-medium">{r.name || "—"}</div>
                                            {r.is_internal && (
                                                <span className="ml-0 mt-0.5 inline-block text-[9.5px] font-bold uppercase tracking-wider text-gold">internal</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 font-mono">{r.owner_email || "—"}</td>
                                        <td className="px-3 py-2 font-mono">{r.owner_domain || "—"}</td>
                                        <td className="px-3 py-2 font-mono">{r.gstin || "—"}</td>
                                        <td className="px-3 py-2 font-mono text-muted-foreground">{r.signup_source || "—"}</td>
                                    </tr>
                                ))}
                                {!loading && (workspaces?.rows || []).length === 0 && (
                                    <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">No workspaces match the filter.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {workspaces?.top_domains?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            <span className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">Top customer domains:</span>
                            {workspaces.top_domains.map((d) => (
                                <span key={d.domain} className="text-[11.5px] font-mono px-2 py-0.5 border border-border bg-muted/30" style={{ borderRadius: 2 }}>
                                    {d.domain} · <span className="text-foreground font-bold">{d.count}</span>
                                </span>
                            ))}
                        </div>
                    )}
                </Section>

                {/* DEMO ACTIVITY */}
                <Section
                    kicker="DEMO ACCOUNT · ACTIVITY"
                    title="Demo logins — last N days"
                    right={
                        <Select value={demoWindow} onValueChange={setDemoWindow}>
                            <SelectTrigger className="h-9 w-[140px]" data-testid="demo-window"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="1">Last 24 hours</SelectItem>
                                <SelectItem value="7">Last 7 days</SelectItem>
                                <SelectItem value="14">Last 14 days</SelectItem>
                                <SelectItem value="30">Last 30 days</SelectItem>
                                <SelectItem value="90">Last 90 days</SelectItem>
                            </SelectContent>
                        </Select>
                    }
                >
                    <div className="grid grid-cols-2 gap-3">
                        <div className="border border-border bg-card p-4" style={{ borderRadius: 2 }}>
                            <div className="text-[10.5px] font-bold tracking-wider uppercase text-muted-foreground mb-2">Per-day</div>
                            {demo?.per_day?.length ? (
                                <div className="space-y-1.5">
                                    {demo.per_day.map((d) => (
                                        <div key={d.day} className="flex items-center gap-3 text-[12px] font-mono">
                                            <span className="w-[90px] text-muted-foreground">{d.day}</span>
                                            <div className="flex-1 h-[8px] bg-muted/40">
                                                <div className="h-full bg-foreground" style={{ width: `${Math.min(100, d.count * 12)}%` }} />
                                            </div>
                                            <span className="w-[40px] text-right tabular-nums">{d.count}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-[12px] text-muted-foreground font-mono">No demo logins in window.</div>
                            )}
                        </div>
                        <div className="border border-border bg-card p-4" style={{ borderRadius: 2 }}>
                            <div className="text-[10.5px] font-bold tracking-wider uppercase text-muted-foreground mb-2">Recent sessions</div>
                            <div className="space-y-1 max-h-[260px] overflow-y-auto">
                                {(demo?.recent || []).map((r, i) => (
                                    <div key={i} className="text-[11.5px] font-mono border-b border-border/60 py-1.5">
                                        <div>{r.ts?.slice(0, 19).replace("T", " ")} · <span className="text-muted-foreground">{r.ip || "—"}</span></div>
                                        <div className="text-muted-foreground text-[10.5px] truncate">{r.user_agent || "—"}</div>
                                    </div>
                                ))}
                                {(demo?.recent || []).length === 0 && (
                                    <div className="text-[12px] text-muted-foreground">No sessions recorded.</div>
                                )}
                            </div>
                        </div>
                    </div>
                </Section>

                {/* SIGNUP FUNNEL */}
                <Section
                    kicker="SIGNUP · FUNNEL"
                    title="Create-your-free-workspace funnel"
                    right={
                        <Select value={funnelWindow} onValueChange={setFunnelWindow}>
                            <SelectTrigger className="h-9 w-[140px]" data-testid="funnel-window"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="7">Last 7 days</SelectItem>
                                <SelectItem value="30">Last 30 days</SelectItem>
                                <SelectItem value="90">Last 90 days</SelectItem>
                                <SelectItem value="365">Last 365 days</SelectItem>
                            </SelectContent>
                        </Select>
                    }
                >
                    <div className="border border-border bg-card overflow-x-auto" style={{ borderRadius: 2 }}>
                        <table className="w-full text-[12.5px]">
                            <thead className="bg-muted/40 text-muted-foreground">
                                <tr className="text-left">
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-wider">Time</th>
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-wider">Stage</th>
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-wider">Email</th>
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-wider">IP</th>
                                    <th className="px-3 py-2 font-mono text-[10.5px] uppercase tracking-wider">Reason / Org</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(funnel?.recent || []).map((r, i) => {
                                    const stageColor =
                                        r.kind === "signup_completed" ? "text-positive" :
                                        r.kind === "signup_failed" ? "text-red-600" :
                                        "text-foreground";
                                    return (
                                        <tr key={i} className="border-t border-border">
                                            <td className="px-3 py-2 font-mono">{r.ts?.slice(0, 19).replace("T", " ")}</td>
                                            <td className={`px-3 py-2 font-mono uppercase ${stageColor}`}>{r.kind?.replace("signup_", "")}</td>
                                            <td className="px-3 py-2 font-mono">{r.email || "—"}</td>
                                            <td className="px-3 py-2 font-mono text-muted-foreground">{r.ip || "—"}</td>
                                            <td className="px-3 py-2 font-mono text-muted-foreground">
                                                {r.extra?.reason || r.extra?.org_name || "—"}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {(funnel?.recent || []).length === 0 && (
                                    <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No signup events in window.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Section>

                <div className="mt-6 text-[10.5px] font-mono text-muted-foreground">
                    $ click refresh to reload · authenticated as super admin · timestamps in UTC
                </div>
            </PageBody>
        </>
    );
}
